"use client";

import { Application } from "@splinetool/runtime";

import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";

interface WelcomeMovingFigure {
  startRef: React.RefObject<HTMLElement | null>;
  endRef: React.RefObject<HTMLElement | null>;
  currentPosition: DOMRect | null;
  setStartRef: (element: HTMLElement | null) => void;
  setEndRef: (element: HTMLElement | null) => void;
  updateCurrentPosition: () => void;
}

interface ContextValue {
  welcomeVisible: boolean;
  welcomeHeight: number;
  setWelcomeVisible: (visible: boolean) => void;
  setWelcomeHeight: (height: number) => void;
  welcomeMovingFigure: WelcomeMovingFigure;
  mainFigureSplineRef: Application | null;
  setMainFigureSplineRef: (spline: Application) => void;
}

const defaultMovingFigure: WelcomeMovingFigure = {
  startRef: { current: null },
  endRef: { current: null },
  currentPosition: null,
  setStartRef: () => {},
  setEndRef: () => {},
  updateCurrentPosition: () => {},
};

const defaultContext: Omit<
  ContextValue,
  "setWelcomeVisible" | "setWelcomeHeight" | "setMainFigureSplineRef"
> = {
  welcomeVisible: false,
  welcomeHeight: 0,
  welcomeMovingFigure: defaultMovingFigure,
  mainFigureSplineRef: null,
};

const Context = createContext<ContextValue>({
  ...defaultContext,
  setWelcomeVisible: () => {},
  setWelcomeHeight: () => {},
  setMainFigureSplineRef: () => {},
});

interface Props {
  children: React.ReactNode;
}

export function ClientProvider({ children }: Props) {
  const [welcomeVisible, setWelcomeVisible] = useState(
    defaultContext.welcomeVisible
  );
  const [welcomeHeight, setWelcomeHeight] = useState(
    defaultContext.welcomeHeight
  );

  const [mainFigureSplineRef, setMainFigureSplineRef] =
    useState<Application | null>(defaultContext.mainFigureSplineRef);

  const startRef = useRef<HTMLElement | null>(null);
  const endRef = useRef<HTMLElement>(null);
  const [currentPosition, setCurrentPosition] = useState<DOMRect | null>(null);

  const updateCurrentPosition = useCallback(() => {
    if (startRef.current) {
      setCurrentPosition(startRef.current.getBoundingClientRect());
    }
  }, []);
  const setStartRef = useCallback(
    (element: HTMLElement | null) => {
      startRef.current = element;
      updateCurrentPosition();
    },
    [updateCurrentPosition]
  );

  const setEndRef = useCallback(
    (element: HTMLElement | null) => {
      endRef.current = element;
      updateCurrentPosition();
    },
    [updateCurrentPosition]
  );

  const welcomeMovingFigure: WelcomeMovingFigure = {
    startRef,
    endRef,
    currentPosition,
    setStartRef,
    setEndRef,
    updateCurrentPosition,
  };

  return (
    <Context.Provider
      value={{
        welcomeVisible,
        setWelcomeVisible,
        welcomeHeight,
        setWelcomeHeight,
        welcomeMovingFigure,
        setMainFigureSplineRef,
        mainFigureSplineRef,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useClientContext() {
  return useContext(Context);
}
